import React, { useEffect, useState, useCallback, useMemo } from "react";
import Content from "../../../Components/Dashboard/Content/Content";
import ExportToExcel from "../../../Utils/ExportCsv";
import Loader from "../../../Utils/Loader";
import { Pencil, Pointer, RefreshCcw, Eye, EyeOff, Shield, X } from "lucide-react";
import Table from "../../../Components/ExtraComponents/Tables/FullDataTable";
import {
  All_Panel_List,
  Close_Admin_Panel,
} from "../../../ReduxStore/Slice/Superadmin/SuperAdminSlice";
import { useDispatch } from "react-redux";
import ToastButton from "../../../Components/ExtraComponents/Alert_Toast";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { UPDATE_PanelName } from "../../../Service/superadminNew";
import Modal from "../../../Components/ExtraComponents/Modal";

// Constants
const ADMIN_PASSWORD = "7700";
const PRIVILEGED_USERS = ["superadmin", "sj"];

const THEME_OPTIONS = [
  { value: "dark", label: "Dark Theme" },
  { value: "light", label: "Light Theme" },
    { value: "header-full header-blue", label: "Full Header Blue" },
  { value: "ocean-blue", label: "Ocean Blue" },
  { value: "indigo-tech", label: "Indigo Tech" },
  { value: "teal-corporate", label: "Teal Corporate" },
  { value: "sunset-warm", label: "Sunset Warm" },
  { value:"rose-elegant", label: "Rose Elegant"},
  { value: "emerald-nature", label: "Emerald Nature" },
  { value: "purple-elegant", label: "Purple Elegant" },
  { value: "horizontal-layout dark", label: "Horizontal Dark" },
  { value: "horizontal-layout light", label: "Horizontal Light" },
  { value: "horizontal-layout ocean-blue", label: "Horizontal Ocean Blue" },

];
const IMAGE_MAP = {
  dark: "/assets/images/dark.png",
  light: "/assets/images/light.png",
  "header-full header-blue": "/assets/images/full-header-blue.png",
  "ocean-blue": "/assets/images/ocean-blue.png",
  "indigo-tech": "/assets/images/indigo-tech.png",
  "teal-corporate": "/assets/images/teal-corporate.png",
  "sunset-warm": "/assets/images/sunset-warm.png",
  "rose-elegant": "/assets/images/rose-elegant.png",
  "emerald-nature": "/assets/images/emerald-nature.png",
  "purple-elegant": "/assets/images/purple-elegant.png",
  "horizontal-layout dark": "/assets/images/horizontal-dark.png",
  "horizontal-layout light": "/assets/images/horizontal-light.png",
  "horizontal-layout ocean-blue": "/assets/images/horizontal-ocean-blue.png",
 
};

// Custom Password Modal Component
const PasswordModal = ({ isOpen, onClose, onConfirm, title, message, isLoading }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    
    if (!password.trim()) {
      setError("Password is required");
      return;
    }
    
    if (password !== ADMIN_PASSWORD) {
      setError("Invalid password. Please try again.");
      setPassword("");
      return;
    }
    
    onConfirm(password);
    setPassword("");
    setError("");
  };

  const handleClose = () => {
    setPassword("");
    setError("");
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-warning text-dark">
            <div className="d-flex align-items-center">
              <Shield className="me-2" size={20} />
              <h5 className="modal-title mb-0">{title}</h5>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={isLoading}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              <div className="text-center mb-4">
                <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" 
                     style={{ width: '60px', height: '60px' }}>
                  <Shield className="text-warning" size={30} />
                </div>
              </div>
              
              <p className="text-center mb-4 text-muted">{message}</p>
              
              <div className="mb-3">
                <label className="form-label fw-semibold">Admin Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
                    placeholder="Enter admin password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {error && <div className="invalid-feedback d-block">{error}</div>}
              </div>
            </div>
            
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-warning"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Shield size={16} className="me-2" />
                    Verify & Continue
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AdminsList = () => {
  const dispatch = useDispatch();
  const user_details = JSON.parse(localStorage.getItem("user_details"));

  // State management
  const [state, setState] = useState({
    showThemeModal: false,
    showPasswordModal: false,
    panelId: "",
    searchInput: "",
    statusData: "",
    selectedTheme: "",
    isLoading: false,
    passwordAction: null,
  });

  const [themeData, setThemeData] = useState({ 
    loading: true, 
    data: [] 
  });

  // Memoized calculations
  const filteredData = useMemo(() => {
    if (!themeData.data.length) return [];

    return themeData.data.filter((item) => {
      const matchSearch = !state.searchInput || 
        item.panel_name.toLowerCase().includes(state.searchInput.toLowerCase()) ||
        item.domain.toLowerCase().includes(state.searchInput.toLowerCase());

      const matchStatus = !state.statusData || 
        item.is_active === Number(state.statusData);

      return matchSearch && matchStatus;
    });
  }, [themeData.data, state.searchInput, state.statusData]);

  const excelData = useMemo(() => {
    return filteredData.map((item) => ({
      "Panel Name": item.panel_name,
      "Domain": item.domain,
      "Theme Name": item.theme_name,
      "Status": item.is_active === 0 ? "Active" : "Inactive",
    }));
  }, [filteredData]);

  const isPrivilegedUser = useMemo(() => 
    PRIVILEGED_USERS.includes(user_details?.UserName), 
    [user_details?.UserName]
  );

  // Fetch data
  const fetchAllPanels = useCallback(async () => {
    try {
      setThemeData(prev => ({ ...prev, loading: true }));
      
      const response = await dispatch(All_Panel_List()).unwrap();
      
      if (response.status && response.data) {
        setThemeData({
          loading: false,
          data: response.data,
        });
      } else {
        setThemeData({
          loading: false,
          data: [],
        });
      }
    } catch (error) {
      console.error("Error fetching panels:", error);
      setThemeData({
        loading: false,
        data: [],
      });
      toast.error("Failed to fetch panels");
    }
  }, [dispatch]);

  // Effects
  useEffect(() => {
    fetchAllPanels();
  }, [fetchAllPanels]);

  // Event handlers
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const openThemeModal = useCallback((panelId) => {
    updateState({ 
      panelId, 
      showThemeModal: true, 
      selectedTheme: "" 
    });
  }, [updateState]);

  const closeThemeModal = useCallback(() => {
    updateState({ 
      showThemeModal: false, 
      panelId: "", 
      selectedTheme: "" 
    });
  }, [updateState]);

  const openPasswordModal = useCallback((action) => {
    updateState({ 
      showPasswordModal: true, 
      passwordAction: action 
    });
  }, [updateState]);

  const closePasswordModal = useCallback(() => {
    updateState({ 
      showPasswordModal: false, 
      passwordAction: null,
      isLoading: false
    });
  }, [updateState]);

  // Handle password confirmation
  const handlePasswordConfirm = useCallback(async (password) => {
    if (!state.passwordAction) return;

    updateState({ isLoading: true });

    try {
      const { domain, newStatus } = state.passwordAction;
      
      const request = {
        domain: domain,
        status: newStatus,
        Name: user_details?.UserName || "Unknown",
      };

      const response = await dispatch(Close_Admin_Panel(request)).unwrap();

      if (response?.status) {
        toast.success(response.msg || "Panel status updated successfully");
        fetchAllPanels();
      } else {
        toast.error(response?.msg || "Failed to update panel status");
        fetchAllPanels(); // Refresh to reset toggle
      }
    } catch (error) {
      console.error("Toggle company error:", error);
      toast.error("An error occurred while updating panel status");
      fetchAllPanels(); // Refresh to reset toggle
    } finally {
      closePasswordModal();
    }
  }, [state.passwordAction, dispatch, user_details?.UserName, fetchAllPanels, closePasswordModal, updateState]);

  // Update theme function
  const handleUpdateTheme = useCallback(async () => {
    if (!state.selectedTheme) {
      toast.error("Please select a theme");
      return;
    }

    if (!user_details?.token || !user_details?.UserName) {
      toast.error("User authentication required");
      return;
    }

    try {
      updateState({ isLoading: true });

      const request = {
        userid: state.panelId,
        theme_name: state.selectedTheme,
        token: user_details.token,
        UserName: user_details.UserName,
      };

      const response = await UPDATE_PanelName(request);
      
      if (response?.status) {
        toast.success(response.msg || "Theme updated successfully");
        closeThemeModal();
        fetchAllPanels();
      } else {
        toast.error(response?.msg || "Failed to update theme");
      }
    } catch (error) {
      console.error("Theme update error:", error);
      toast.error("An error occurred while updating the theme");
    } finally {
      updateState({ isLoading: false });
    }
  }, [state.selectedTheme, state.panelId, user_details, updateState, closeThemeModal, fetchAllPanels]);

  // Handle toggle company with custom password modal
  const handleToggleCompany = useCallback((domain, isCurrentlyActive) => {
    const newStatus = isCurrentlyActive ? 1 : 0; // 0 = active, 1 = inactive
    const action = isCurrentlyActive ? "deactivate" : "activate";
    
    openPasswordModal({
      domain,
      newStatus,
      action
    });
  }, [openPasswordModal]);

  // Table columns configuration with better styling
  const columns = useMemo(() => [
    {
      name: "Panel Name",
      selector: (row) => row.panel_name,
      cell: (row) => (
        <div>
          <Link
            to={`${row.domain}/#/login`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none fw-medium text-primary fs-6"
            title={`Open ${row.panel_name} in new tab`}
          >
            {row.panel_name}
          </Link>
          <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
            {row.domain}
          </small>
        </div>
      ),
      width: "250px",
      sortable: true,
    },
    {
      name: "Current Theme",
      selector: (row) => row.theme_name,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div>
          <span className="badge bg-info text-dark px-2 py-1">
            {row.theme_name || 'Default'}
          </span>
        </div>
      ),
    },
    {
      name: "Status",
      cell: (row) => {
        const isActive = Number(row.is_active) === 0;
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                checked={isActive}
                onChange={() => handleToggleCompany(row.domain, isActive)}
                title={`Click to ${isActive ? 'deactivate' : 'activate'} panel`}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'} px-2 py-1`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
      width: "150px",
      center: true,
    },
    {
      name: "Change Theme",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          onClick={() => openThemeModal(row._id)}
          title="Change panel theme"
        >
          <Pointer size={14} />
          <span className="d-none d-md-inline">Theme</span>
        </button>
      ),
      width: "120px",
      center: true,
    },
    {
      name: "Edit",
      cell: (row) => (
        <Link 
          to={`/super/panel/edit/${row._id}`} 
          state={row}
          className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
          title="Edit panel details"
        >
          <Pencil size={14} />
          <span className="d-none d-md-inline">Edit</span>
        </Link>
      ),
      width: "100px",
      center: true,
    },
  ], [handleToggleCompany, openThemeModal]);

  // Render loading state
  if (themeData.loading) {
    return (
      <Content Page_title="Company Panels Management">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Loader />
        </div>
      </Content>
    );
  }

  return (
    <Content
      Page_title="Company Panels Management"
      button_title={isPrivilegedUser ? "Add New Panel" : ""}
      route="/super/panel/add"
      button_status={isPrivilegedUser}
    >

    
      {/* Enhanced Filters Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-lg-4 col-md-6">
              <label className="form-label fw-semibold text-dark">
                <i className="bi bi-search me-1"></i>Search Panels
              </label>
              <input
                type="text"
                placeholder="Search by panel name or domain..."
                className="form-control form-control-lg"
                value={state.searchInput}
                onChange={(e) => updateState({ searchInput: e.target.value })}
              />
            </div>

            <div className="col-lg-2 col-md-6">
              <label className="form-label fw-semibold text-dark">Filter Status</label>
              <select
                className="form-select form-select-lg"
                value={state.statusData}
                onChange={(e) => updateState({ statusData: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="0">Active Only</option>
                <option value="1">Inactive Only</option>
              </select>
            </div>

            <div className="col-lg-3 col-md-6">
              <div>
                <ExportToExcel
                  className="btn btn-success btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                  apiData={excelData}
                  fileName="Company_Panels_Export"
                />
              </div>
            </div>

          
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span className="badge bg-primary  px-3 py-2">
            Showing {filteredData.length} of {themeData.data.length} panels
          </span>
        </div>
        <div className="text-muted">
          <small>Last updated: {new Date().toLocaleTimeString()}</small>
        </div>
      </div>

      {/* Enhanced Data Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <Table 
            columns={columns} 
            data={filteredData}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[5, 10, 15, 25, 50]}
            highlightOnHover
            striped
            responsive
            noDataMessage="No panels found matching your search criteria"
            customStyles={{
              table: {
                style: {
                  backgroundColor: 'transparent',
                }
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#495057'
                }
              },
              rows: {
                style: {
                  fontSize: '13px',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'default'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Theme Update Modal */}
    <Modal
  isOpen={state.showThemeModal}
  size="xl"
  title="Update Panel Theme"
  hideBtn={true}
  handleClose={closeThemeModal}
  backdrop={true}
  onHide={closeThemeModal} 
>
            <div className="text-center mb-4">
            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" 
                 style={{ width: '60px', height: '60px' }}>
              <Pointer className="text-primary" size={24} />
            </div>
            <h6 className="mt-3 mb-0">Choose New Theme</h6>
            <small className="text-muted">Select a theme for this panel</small>
          </div>
  <div className="mb-4">
  <label className="form-label fw-semibold">Available Themes</label>

  <div className="row g-3 mt-2">
    {THEME_OPTIONS.map((theme) => (
      <div className="col-md-4" key={theme.value}>
        <div
          className={`card theme-card ${theme.value}`}
          style={{
            cursor: "pointer",
            border: state.selectedTheme === theme.value ? "2px solid #0d6efd" : "",
          }}
          onClick={() => updateState({ selectedTheme: theme.value })}
        >
          {/* Optional image for theme */}
               <div className="card-body text-center">
           
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="themeRadio"
                id={`theme-${theme.value}`}
                value={theme.value}
                checked={state.selectedTheme === theme.value}
                onChange={() => updateState({ selectedTheme: theme.value })}
              />
            <h6 className="card-title mb-2">{theme.label}</h6>
            </div>
          </div>
            <img
  src={IMAGE_MAP[theme.value]}
  className="card-img-top"
  alt={theme.label}
/>


     
        </div>
      </div>
    ))}
  </div>
</div>

        <div className="p-4">
          {/* <div className="text-center mb-4">
            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" 
                 style={{ width: '60px', height: '60px' }}>
              <Pointer className="text-primary" size={24} />
            </div>
            <h6 className="mt-3 mb-0">Choose New Theme</h6>
            <small className="text-muted">Select a theme for this panel</small>
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold">Available Themes</label>
            <select
              className="form-select form-select-lg"
              value={state.selectedTheme}
              onChange={(e) => updateState({ selectedTheme: e.target.value })}
              disabled={state.isLoading}
            >
              <option value="">-- Choose a Theme --</option>
              {THEME_OPTIONS.map((theme) => (
                <option key={theme.value} value={theme.value}>
                  {theme.label}
                </option>
              ))}
            </select>
          </div> */}

          <div className="d-flex gap-3 justify-content-end">
            <button
              type="button"
              className="btn btn-light px-4"
              onClick={closeThemeModal}
              disabled={state.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary px-4"
              onClick={handleUpdateTheme}
              disabled={state.isLoading || !state.selectedTheme}
            >
              {state.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Updating...
                </>
              ) : (
                <>
                  <Pointer size={16} className="me-2" />
                  Update Theme
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Custom Password Modal */}
      <PasswordModal
        isOpen={state.showPasswordModal}
        onClose={closePasswordModal}
        onConfirm={handlePasswordConfirm}
        title="Security Verification Required"
        message={`Please enter admin password to ${state.passwordAction?.action || 'proceed with'} this panel.`}
        isLoading={state.isLoading}
      />

      {/* Toast Notifications */}
      <ToastButton />
    </Content>
  );
};

export default AdminsList;